import { useState, useEffect } from "react";
import { Save, Plus, Trash2, ChevronDown, ChevronRight, Layers, Trophy, BarChart3, Star as StarIcon, Award, Gift, Crown, Smartphone, HelpCircle, MessageCircle, FileText } from "lucide-react";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";

/* Reusable small inputs ----------------------------------------------------- */
const In = ({ value, onChange, ...rest }) => (
  <input
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-9 px-2.5 border border-gray-200 rounded-sm text-sm"
    {...rest}
  />
);
const InNum = ({ value, onChange, ...rest }) => (
  <input
    type="number"
    value={value ?? 0}
    onChange={(e) => onChange(Number(e.target.value))}
    className="w-full h-9 px-2.5 border border-gray-200 rounded-sm text-sm tabular"
    {...rest}
  />
);
const Area = ({ value, onChange, rows = 2, ...rest }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="w-full px-2.5 py-2 border border-gray-200 rounded-sm text-sm"
    {...rest}
  />
);
const Color = ({ value, onChange }) => (
  <div className="flex gap-1">
    <input type="color" value={value || "#19a55c"} onChange={(e) => onChange(e.target.value)} className="h-9 w-10 border border-gray-200 rounded-sm" />
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 h-9 px-2 border border-gray-200 rounded-sm text-xs font-mono" />
  </div>
);
const Check = ({ checked, onChange, label, testId }) => (
  <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
    {label}
  </label>
);
const L = ({ children }) => <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">{children}</label>;

/* Collapsible Section ------------------------------------------------------ */
function Section({ id, title, icon: Icon, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-gray-200 rounded-sm mb-4" data-testid={`section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2.5">
          <Icon size={16} className="text-emerald-500" />
          <h3 className="font-semibold text-sm text-gray-900">{title}</h3>
          {typeof count === "number" && (
            <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-600 font-semibold">
              {count} {count === 1 ? "item" : "items"}
            </span>
          )}
        </div>
        {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

/* Icon names — common lucide-react icons we support in the map ------------- */
const ICON_OPTIONS = [
  "Gift","Zap","ShieldCheck","Headphones","Trophy","Users","Gamepad2","Banknote",
  "BadgeCheck","Lock","Scale","Star","Sparkles","Crown","Smartphone","MessageCircle",
  "Mail","Phone","Send","Clock","Heart","Flame","Rocket","Target","Award","Percent",
];
const IconSelect = ({ value, onChange }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm">
    <option value="">— none —</option>
    {ICON_OPTIONS.map((i) => (<option key={i} value={i}>{i}</option>))}
  </select>
);

/* Generic list editor ------------------------------------------------------
   Every row needs a stable React `key` for correct reconciliation across
   add / delete operations. We use `it.id` when present (most blanks define
   one), otherwise auto-generate a unique id the FIRST time we see a row
   that lacks one — and patch it back into the parent state via onChange so
   subsequent renders are stable. */
function ListEditor({ items, onChange, renderRow, blank, minItems = 0, addLabel = "Add item", testIdPrefix = "list" }) {
  const list = Array.isArray(items) ? items : [];

  // Hydrate any rows that lack an id on first render — patches parent state
  // exactly once per legacy row. This eliminates the "input values jump
  // between rows when I delete one" bug that comes from index-keyed lists.
  useEffect(() => {
    if (!list.length) return;
    const missing = list.some((it) => !it || !it.id);
    if (!missing) return;
    const hydrated = list.map((it, i) => (it && it.id ? it : {
      ...(it || {}),
      id: `${testIdPrefix}-legacy-${i}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    }));
    onChange(hydrated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.length]);

  const update = (i, next) => onChange(list.map((it, idx) => (idx === i ? next : it)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([
    ...list,
    {
      ...blank,
      // Always give new rows a unique id, even when blank doesn't define one
      id: `${blank.id || testIdPrefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    },
  ]);
  return (
    <div className="space-y-3">
      {list.map((it, i) => (
        <div key={it.id || i} className="border border-gray-200 rounded-sm p-3 bg-gray-50/50" data-testid={`${testIdPrefix}-row-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">#{i + 1}</span>
            <button type="button" onClick={() => remove(i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded-sm" disabled={list.length <= minItems} data-testid={`${testIdPrefix}-del-${i}`}>
              <Trash2 size={13} />
            </button>
          </div>
          {renderRow(it, (patch) => update(i, { ...it, ...patch }))}
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-semibold bg-gray-900 hover:bg-gray-800 text-white rounded-sm" data-testid={`${testIdPrefix}-add`}>
        <Plus size={13} /> {addLabel}
      </button>
    </div>
  );
}

/* Main Component ----------------------------------------------------------- */
export default function ConfigHomeContent() {
  const { form, set, save, loading } = useSettings();

  if (!form) {
    return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;
  }

  const onSave = () => save();

  return (
    <div data-testid="config-home-content-page">
      <PageHeader title="Home Content" subtitle="Admin-controlled content for Home sections, Promotions, VIP, APK & Contact pages" />
      <ConfigTabs />

      {/* Trust Strip */}
      <Section id="trust" title="Home · Trust Strip (4 USPs under banner)" icon={Award} count={(form.home_trust_items || []).length} defaultOpen>
        <div className="mb-3">
          <Check checked={form.home_trust_enabled !== false} onChange={(v) => set("home_trust_enabled", v)} label="Show Trust Strip on home page" testId="cfg-home-trust-enabled" />
        </div>
        <ListEditor
          items={form.home_trust_items}
          onChange={(v) => set("home_trust_items", v)}
          blank={{ id: "t", icon: "Gift", title_full: "", title_short: "", sub: "", color: "#19a55c", enabled: true, order: 99 }}
          addLabel="Add trust item"
          testIdPrefix="trust"
          renderRow={(it, patch) => (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-start">
              <div className="col-span-2 md:col-span-1"><L>Icon</L><IconSelect value={it.icon} onChange={(v) => patch({ icon: v })} /></div>
              <div><L>Full title (desktop)</L><In value={it.title_full} onChange={(v) => patch({ title_full: v })} /></div>
              <div><L>Short title (mobile)</L><In value={it.title_short} onChange={(v) => patch({ title_short: v })} /></div>
              <div className="col-span-2 md:col-span-1"><L>Subtitle</L><In value={it.sub} onChange={(v) => patch({ sub: v })} /></div>
              <div><L>Color</L><Color value={it.color} onChange={(v) => patch({ color: v })} /></div>
              <div><L>Enabled</L><Check checked={it.enabled !== false} onChange={(v) => patch({ enabled: v })} label="Visible" /></div>
            </div>
          )}
        />
      </Section>

      {/* Live Wins */}
      <Section id="live-wins" title="Home · Live Wins Ticker" icon={Trophy} count={(form.home_live_wins_items || []).length}>
        <div className="mb-3">
          <Check checked={form.home_live_wins_enabled !== false} onChange={(v) => set("home_live_wins_enabled", v)} label="Show Live Wins ticker" testId="cfg-home-wins-enabled" />
        </div>
        <ListEditor
          items={form.home_live_wins_items}
          onChange={(v) => set("home_live_wins_items", v)}
          blank={{ user: "", game: "", amount: 1000 }}
          addLabel="Add win entry"
          testIdPrefix="wins"
          renderRow={(it, patch) => (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div><L>Username (masked)</L><In value={it.user} onChange={(v) => patch({ user: v })} placeholder="Ra****l" /></div>
              <div><L>Game</L><In value={it.game} onChange={(v) => patch({ game: v })} placeholder="Aviator" /></div>
              <div><L>Amount (৳)</L><InNum value={it.amount} onChange={(v) => patch({ amount: v })} /></div>
            </div>
          )}
        />
      </Section>

      {/* Stats Counter */}
      <Section id="stats" title="Home · Stats Counter (4 big numbers)" icon={BarChart3} count={(form.home_stats_items || []).length}>
        <div className="mb-3">
          <Check checked={form.home_stats_enabled !== false} onChange={(v) => set("home_stats_enabled", v)} label="Show Stats counter" testId="cfg-home-stats-enabled" />
        </div>
        <ListEditor
          items={form.home_stats_items}
          onChange={(v) => set("home_stats_items", v)}
          blank={{ id: "s", icon: "Users", end: 100, prefix: "", suffix: "+", label: "Players", color: "#19a55c", enabled: true, order: 99 }}
          addLabel="Add stat"
          testIdPrefix="stats"
          renderRow={(it, patch) => (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-start">
              <div><L>Icon</L><IconSelect value={it.icon} onChange={(v) => patch({ icon: v })} /></div>
              <div><L>End value</L><InNum value={it.end} onChange={(v) => patch({ end: v })} /></div>
              <div><L>Prefix</L><In value={it.prefix} onChange={(v) => patch({ prefix: v })} placeholder="৳" /></div>
              <div><L>Suffix</L><In value={it.suffix} onChange={(v) => patch({ suffix: v })} placeholder="K+" /></div>
              <div className="col-span-2 md:col-span-1"><L>Label</L><In value={it.label} onChange={(v) => patch({ label: v })} /></div>
              <div><L>Color</L><Color value={it.color} onChange={(v) => patch({ color: v })} /></div>
              <div className="col-span-2 md:col-span-6"><Check checked={it.enabled !== false} onChange={(v) => patch({ enabled: v })} label="Visible" /></div>
            </div>
          )}
        />
      </Section>

      {/* Why Choose Us */}
      <Section id="why" title="Home · Why Choose Us (4 trust cards)" icon={StarIcon} count={(form.home_why_items || []).length}>
        <div className="mb-3">
          <Check checked={form.home_why_enabled !== false} onChange={(v) => set("home_why_enabled", v)} label="Show Why Choose Us" testId="cfg-home-why-enabled" />
        </div>
        <ListEditor
          items={form.home_why_items}
          onChange={(v) => set("home_why_items", v)}
          blank={{ id: "w", icon: "ShieldCheck", title: "", short: "", full: "", color: "#19a55c", enabled: true, order: 99 }}
          addLabel="Add why-choose item"
          testIdPrefix="why"
          renderRow={(it, patch) => (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-start">
              <div><L>Icon</L><IconSelect value={it.icon} onChange={(v) => patch({ icon: v })} /></div>
              <div><L>Title</L><In value={it.title} onChange={(v) => patch({ title: v })} /></div>
              <div><L>Color</L><Color value={it.color} onChange={(v) => patch({ color: v })} /></div>
              <div><Check checked={it.enabled !== false} onChange={(v) => patch({ enabled: v })} label="Visible" /></div>
              <div className="col-span-2"><L>Short description (mobile)</L><Area rows={2} value={it.short} onChange={(v) => patch({ short: v })} /></div>
              <div className="col-span-2"><L>Full description (desktop)</L><Area rows={2} value={it.full} onChange={(v) => patch({ full: v })} /></div>
            </div>
          )}
        />
      </Section>

      {/* License Badges */}
      <Section id="license" title="Home · License Badges" icon={Award} count={(form.home_license_badges || []).length}>
        <div className="mb-3">
          <Check checked={form.home_license_enabled !== false} onChange={(v) => set("home_license_enabled", v)} label="Show License badges strip" testId="cfg-home-license-enabled" />
        </div>
        <ListEditor
          items={form.home_license_badges}
          onChange={(v) => set("home_license_badges", v)}
          blank={{ id: "b", icon: "ShieldCheck", text: "", label: "", sub: "", color: "#19a55c", enabled: true, order: 99 }}
          addLabel="Add badge"
          testIdPrefix="license"
          renderRow={(it, patch) => (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-start">
              <div><L>Icon (or leave blank + use Text)</L><IconSelect value={it.icon} onChange={(v) => patch({ icon: v })} /></div>
              <div><L>Text (e.g. "18+")</L><In value={it.text} onChange={(v) => patch({ text: v })} /></div>
              <div><L>Label</L><In value={it.label} onChange={(v) => patch({ label: v })} /></div>
              <div className="col-span-2 md:col-span-1"><L>Sub-label</L><In value={it.sub} onChange={(v) => patch({ sub: v })} /></div>
              <div><L>Color</L><Color value={it.color} onChange={(v) => patch({ color: v })} /></div>
              <div><L>Enabled</L><Check checked={it.enabled !== false} onChange={(v) => patch({ enabled: v })} label="Visible" /></div>
            </div>
          )}
        />
      </Section>

      {/* Promotions */}
      <Section id="promotions" title="Promotions Page" icon={Gift} count={(form.promotions_items || []).length}>
        <div className="mb-4">
          <Check checked={form.promotions_enabled !== false} onChange={(v) => set("promotions_enabled", v)} label="Enable promotions page" testId="cfg-promotions-enabled" />
        </div>

        {/* Hero copy & CTAs */}
        <div className="mb-5 p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Hero — Headline & CTAs</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><L>Kicker (top badge)</L><In value={form.promotions_hero_kicker} onChange={(v) => set("promotions_hero_kicker", v)} placeholder="Live Bonuses & Rewards" /></div>
            <div><L>Headline · part 1</L><In value={form.promotions_hero_title_1} onChange={(v) => set("promotions_hero_title_1", v)} placeholder="Bigger wins," /></div>
            <div><L>Headline · part 2 (gradient text)</L><In value={form.promotions_hero_title_2} onChange={(v) => set("promotions_hero_title_2", v)} placeholder="bolder bonuses." /></div>
            <div className="md:col-span-2"><L>Subtitle</L><Area value={form.promotions_hero_subtitle} onChange={(v) => set("promotions_hero_subtitle", v)} rows={2} /></div>
            <div><L>Primary CTA text</L><In value={form.promotions_hero_cta_primary_text} onChange={(v) => set("promotions_hero_cta_primary_text", v)} placeholder="Claim Top Bonus" /></div>
            <div><L>Secondary CTA text</L><In value={form.promotions_hero_cta_secondary_text} onChange={(v) => set("promotions_hero_cta_secondary_text", v)} placeholder="Explore VIP Club" /></div>
            <div className="md:col-span-2"><L>Secondary CTA URL</L><In value={form.promotions_hero_cta_secondary_url} onChange={(v) => set("promotions_hero_cta_secondary_url", v)} placeholder="/vip-club" /></div>
          </div>
        </div>

        {/* Prize pool tile */}
        <div className="mb-5 p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Hero — Prize Pool Tile</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2"><L>Tile label</L><In value={form.promotions_prize_pool_label} onChange={(v) => set("promotions_prize_pool_label", v)} placeholder="Total Prize Pool · This Week" /></div>
            <div><L>Currency symbol</L><In value={form.promotions_prize_pool_currency} onChange={(v) => set("promotions_prize_pool_currency", v)} placeholder="৳" /></div>
            <div className="md:col-span-3"><L>Big amount</L><In value={form.promotions_prize_pool_amount} onChange={(v) => set("promotions_prize_pool_amount", v)} placeholder="50,00,000" /></div>
            <div className="md:col-span-3"><L>Refresh note (under tile)</L><In value={form.promotions_prize_pool_note} onChange={(v) => set("promotions_prize_pool_note", v)} placeholder="Refreshes every Monday · 00:00 Asia/Dhaka" /></div>
          </div>
          <div className="mt-3">
            <L>Breakdown sub-tiles (3 max — Sportsbook / Casino / Slots)</L>
            <ListEditor
              items={form.promotions_prize_pool_breakdown}
              onChange={(v) => set("promotions_prize_pool_breakdown", v)}
              blank={{ label: "", value: "" }}
              addLabel="Add breakdown row"
              testIdPrefix="promo-prize-bd"
              renderRow={(it, patch) => (
                <div className="grid grid-cols-2 gap-2">
                  <div><L>Label</L><In value={it.label} onChange={(v) => patch({ label: v })} placeholder="Sportsbook" /></div>
                  <div><L>Value</L><In value={it.value} onChange={(v) => patch({ value: v })} placeholder="20L" /></div>
                </div>
              )}
            />
          </div>
        </div>

        {/* How to claim steps */}
        <div className="mb-5 p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">"How to claim your bonus" — 3 steps</div>
          <ListEditor
            items={form.promotions_how_steps}
            onChange={(v) => set("promotions_how_steps", v)}
            blank={{ n: "01", title: "", desc: "", icon: "Wallet", color: "#19a55c" }}
            addLabel="Add step"
            testIdPrefix="promo-step"
            renderRow={(it, patch) => (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-start">
                <div><L>Number</L><In value={it.n} onChange={(v) => patch({ n: v })} placeholder="01" /></div>
                <div className="col-span-2"><L>Title</L><In value={it.title} onChange={(v) => patch({ title: v })} placeholder="Sign up & Deposit" /></div>
                <div><L>Color</L><Color value={it.color} onChange={(v) => patch({ color: v })} /></div>
                <div className="md:col-span-3"><L>Description</L><Area rows={2} value={it.desc} onChange={(v) => patch({ desc: v })} /></div>
                <div><L>Icon (Wallet / Gift / CircleDollarSign)</L><IconSelect value={it.icon} onChange={(v) => patch({ icon: v })} /></div>
              </div>
            )}
          />
        </div>

        {/* T&C lines */}
        <div className="mb-5 p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">General Terms — bullet points</div>
          <Area
            rows={6}
            value={(form.promotions_terms_lines || []).join("\n")}
            onChange={(v) => set("promotions_terms_lines", v.split("\n").map((x) => x.trim()).filter(Boolean))}
            placeholder="One bullet per line"
          />
          <div className="text-[10px] text-gray-500 mt-1">One bullet per line. Saved as an array.</div>
        </div>

        <div className="mb-5">
          <L>Quick Stats (4 tiles shown above promo cards)</L>
          <ListEditor
            items={form.promotions_stats}
            onChange={(v) => set("promotions_stats", v)}
            blank={{ value: "", label: "", color: "#19a55c" }}
            addLabel="Add stat"
            testIdPrefix="promo-stat"
            renderRow={(it, patch) => (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><L>Value</L><In value={it.value} onChange={(v) => patch({ value: v })} placeholder="100%" /></div>
                <div><L>Label</L><In value={it.label} onChange={(v) => patch({ label: v })} /></div>
                <div><L>Color</L><Color value={it.color} onChange={(v) => patch({ color: v })} /></div>
              </div>
            )}
          />
        </div>
        <div>
          <L>Promo Cards</L>
          <ListEditor
            items={form.promotions_items}
            onChange={(v) => set("promotions_items", v)}
            blank={{ id: "p", title: "", subtitle: "", cta: "Claim Now", color1: "#19a55c", color2: "#0a3a22", badge: "NEW", enabled: true, order: 99 }}
            addLabel="Add promo card"
            testIdPrefix="promo"
            renderRow={(it, patch) => (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-start">
                <div className="col-span-2"><L>Title</L><In value={it.title} onChange={(v) => patch({ title: v })} /></div>
                <div><L>Badge (NEW / HOT / VIP / EVENT)</L><In value={it.badge} onChange={(v) => patch({ badge: v })} /></div>
                <div><L>CTA Text</L><In value={it.cta} onChange={(v) => patch({ cta: v })} /></div>
                <div className="col-span-2 md:col-span-4"><L>Subtitle</L><In value={it.subtitle} onChange={(v) => patch({ subtitle: v })} /></div>
                <div><L>Gradient color 1</L><Color value={it.color1} onChange={(v) => patch({ color1: v })} /></div>
                <div><L>Gradient color 2</L><Color value={it.color2} onChange={(v) => patch({ color2: v })} /></div>
                <div><Check checked={it.enabled !== false} onChange={(v) => patch({ enabled: v })} label="Visible" /></div>
              </div>
            )}
          />
        </div>
      </Section>

      {/* Forum / Community */}
      <Section id="forum" title="Forum / Community Page" icon={MessageCircle}>
        <div className="mb-4">
          <Check checked={form.forum_enabled !== false} onChange={(v) => set("forum_enabled", v)} label="Enable Forum page" testId="cfg-forum-enabled" />
        </div>

        <div className="mb-5 p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Hero — Headline & CTAs</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><L>Kicker</L><In value={form.forum_hero_kicker} onChange={(v) => set("forum_hero_kicker", v)} placeholder="Community · Live now" /></div>
            <div><L>Headline · part 1</L><In value={form.forum_hero_title_1} onChange={(v) => set("forum_hero_title_1", v)} placeholder="Maxlive88" /></div>
            <div><L>Headline · part 2 (gradient text)</L><In value={form.forum_hero_title_2} onChange={(v) => set("forum_hero_title_2", v)} placeholder="Forum" /></div>
            <div><L>Join button text</L><In value={form.forum_join_cta_text} onChange={(v) => set("forum_join_cta_text", v)} placeholder="Join the Community" /></div>
            <div className="md:col-span-2"><L>Subtitle</L><Area rows={2} value={form.forum_hero_subtitle} onChange={(v) => set("forum_hero_subtitle", v)} /></div>
            <div><L>Join button URL</L><In value={form.forum_join_cta_url} onChange={(v) => set("forum_join_cta_url", v)} placeholder="/register" /></div>
            <div><L>Telegram channel URL</L><In value={form.forum_telegram_url} onChange={(v) => set("forum_telegram_url", v)} placeholder="https://t.me/yourchannel" /></div>
            <div><L>WhatsApp group URL</L><In value={form.forum_whatsapp_url} onChange={(v) => set("forum_whatsapp_url", v)} placeholder="https://wa.me/8801XXXXXXXXX" /></div>
          </div>
        </div>

        <div className="mb-5 p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Community Pulse — Live stats card</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><L>Card label</L><In value={form.forum_pulse_label} onChange={(v) => set("forum_pulse_label", v)} placeholder="Community Pulse" /></div>
            <div><L>Refresh note</L><In value={form.forum_pulse_note} onChange={(v) => set("forum_pulse_note", v)} placeholder="Updated every 30 seconds · Asia/Dhaka" /></div>
            <div><L>Members value</L><In value={form.forum_stats_members_value} onChange={(v) => set("forum_stats_members_value", v)} placeholder="47.2K" /></div>
            <div><L>Threads value</L><In value={form.forum_stats_threads_value} onChange={(v) => set("forum_stats_threads_value", v)} placeholder="40.5K" /></div>
            <div><L>Online value</L><In value={form.forum_stats_online_value} onChange={(v) => set("forum_stats_online_value", v)} placeholder="1,284" /></div>
          </div>
        </div>

        <div className="p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Bottom Banner — Coming-soon CTA</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><L>Kicker</L><In value={form.forum_launch_kicker} onChange={(v) => set("forum_launch_kicker", v)} placeholder="Launching soon · Q2 2026" /></div>
            <div><L>Title</L><In value={form.forum_launch_title} onChange={(v) => set("forum_launch_title", v)} placeholder="The full community platform is coming." /></div>
            <div className="md:col-span-2"><L>Subtitle</L><Area rows={2} value={form.forum_launch_subtitle} onChange={(v) => set("forum_launch_subtitle", v)} /></div>
            <div><L>Telegram button text</L><In value={form.forum_launch_telegram_text} onChange={(v) => set("forum_launch_telegram_text", v)} placeholder="Join Telegram" /></div>
            <div><L>WhatsApp button text</L><In value={form.forum_launch_whatsapp_text} onChange={(v) => set("forum_launch_whatsapp_text", v)} placeholder="WhatsApp" /></div>
            <div className="md:col-span-2"><L>Notify button text</L><In value={form.forum_launch_notify_text} onChange={(v) => set("forum_launch_notify_text", v)} placeholder="Notify me on launch" /></div>
          </div>
        </div>
      </Section>

      {/* Terms & Conditions */}
      <Section id="terms-page" title="Terms & Conditions Page" icon={FileText}>
        <div className="p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Hero & meta</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><L>Version</L><In value={form.terms_version} onChange={(v) => set("terms_version", v)} placeholder="v3.2" /></div>
            <div><L>Last updated</L><In value={form.terms_last_updated} onChange={(v) => set("terms_last_updated", v)} placeholder="January 2026" /></div>
            <div><L>Read time pill</L><In value={form.terms_read_minutes} onChange={(v) => set("terms_read_minutes", v)} placeholder="~5 min read" /></div>
            <div className="md:col-span-3"><L>Hero subtitle</L><Area rows={2} value={form.terms_hero_subtitle} onChange={(v) => set("terms_hero_subtitle", v)} /></div>
          </div>
        </div>

        <div className="mt-3 p-4 rounded-sm bg-gray-50 border border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Bottom CTA card</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><L>Title</L><In value={form.terms_cta_title} onChange={(v) => set("terms_cta_title", v)} placeholder="Have a question about these Terms?" /></div>
            <div className="md:col-span-2"><L>Subtitle</L><Area rows={2} value={form.terms_cta_subtitle} onChange={(v) => set("terms_cta_subtitle", v)} /></div>
            <div><L>Button text</L><In value={form.terms_cta_button_text} onChange={(v) => set("terms_cta_button_text", v)} placeholder="Contact support" /></div>
            <div><L>Button URL</L><In value={form.terms_cta_button_url} onChange={(v) => set("terms_cta_button_url", v)} placeholder="/contact" /></div>
            <div className="md:col-span-2"><L>Footer note</L><In value={form.terms_footer_note} onChange={(v) => set("terms_footer_note", v)} placeholder="Maxlive88 Terms v3.2 · Effective January 2026" /></div>
          </div>
        </div>
      </Section>

      {/* VIP Tiers */}
      <Section id="vip" title="VIP Club Page" icon={Crown} count={(form.vip_tiers || []).length}>
        <div className="mb-3">
          <Check checked={form.vip_enabled !== false} onChange={(v) => set("vip_enabled", v)} label="Enable VIP Club page" testId="cfg-vip-enabled" />
        </div>
        <ListEditor
          items={form.vip_tiers}
          onChange={(v) => set("vip_tiers", v)}
          blank={{ name: "", range: "", min_balance: 0, color: "#19a55c", perks: [], enabled: true, order: 99 }}
          addLabel="Add tier"
          testIdPrefix="vip"
          renderRow={(it, patch) => (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-start">
              <div><L>Tier name</L><In value={it.name} onChange={(v) => patch({ name: v })} placeholder="Gold" /></div>
              <div><L>Range label</L><In value={it.range} onChange={(v) => patch({ range: v })} placeholder="৳50,000 – ৳2,00,000" /></div>
              <div><L>Min balance (auto-tier)</L><InNum value={it.min_balance} onChange={(v) => patch({ min_balance: v })} /></div>
              <div><L>Color</L><Color value={it.color} onChange={(v) => patch({ color: v })} /></div>
              <div className="col-span-2 md:col-span-4"><L>Perks (one per line)</L>
                <Area rows={4} value={(it.perks || []).join("\n")} onChange={(v) => patch({ perks: v.split("\n").map((s) => s.trim()).filter(Boolean) })} />
              </div>
              <div><Check checked={it.enabled !== false} onChange={(v) => patch({ enabled: v })} label="Visible" /></div>
            </div>
          )}
        />
      </Section>

      {/* APK Download */}
      <Section id="apk" title="APK Download Page" icon={Smartphone}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <div className="md:col-span-3"><L>APK download URL</L><In value={form.apk_url} onChange={(v) => set("apk_url", v)} placeholder="https://.../maxlive88.apk" /></div>
          <div><L>Version</L><In value={form.apk_version} onChange={(v) => set("apk_version", v)} placeholder="v2.0" /></div>
          <div><L>Size</L><In value={form.apk_size_mb} onChange={(v) => set("apk_size_mb", v)} placeholder="32 MB" /></div>
          <div><L>Rating</L><In value={form.apk_rating} onChange={(v) => set("apk_rating", v)} placeholder="4.8" /></div>
          <div><L>Downloads (display)</L><In value={form.apk_downloads} onChange={(v) => set("apk_downloads", v)} placeholder="500k+" /></div>
        </div>
        <div className="mb-5">
          <L>App Features (4 shown)</L>
          <ListEditor
            items={form.apk_features}
            onChange={(v) => set("apk_features", v)}
            blank={{ icon: "Zap", title: "", color: "#19a55c", order: 99 }}
            addLabel="Add feature"
            testIdPrefix="apk-feat"
            renderRow={(it, patch) => (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><L>Icon</L><IconSelect value={it.icon} onChange={(v) => patch({ icon: v })} /></div>
                <div><L>Title</L><In value={it.title} onChange={(v) => patch({ title: v })} /></div>
                <div><L>Color</L><Color value={it.color} onChange={(v) => patch({ color: v })} /></div>
              </div>
            )}
          />
        </div>
        <div>
          <L>Install Steps</L>
          <ListEditor
            items={form.apk_steps}
            onChange={(v) => set("apk_steps", v)}
            blank={{ n: "", title: "", desc: "" }}
            addLabel="Add step"
            testIdPrefix="apk-step"
            renderRow={(it, patch) => (
              <div className="grid grid-cols-6 gap-2 items-start">
                <div><L>Number</L><In value={it.n} onChange={(v) => patch({ n: v })} /></div>
                <div className="col-span-2"><L>Title</L><In value={it.title} onChange={(v) => patch({ title: v })} /></div>
                <div className="col-span-3"><L>Description</L><In value={it.desc} onChange={(v) => patch({ desc: v })} /></div>
              </div>
            )}
          />
        </div>
      </Section>

      {/* Contact FAQs */}
      <Section id="faqs" title="Contact Page — FAQs" icon={HelpCircle} count={(form.contact_faqs || []).length}>
        <ListEditor
          items={form.contact_faqs}
          onChange={(v) => set("contact_faqs", v)}
          blank={{ q: "", a: "" }}
          addLabel="Add FAQ"
          testIdPrefix="faq"
          renderRow={(it, patch) => (
            <div className="grid grid-cols-1 gap-2">
              <div><L>Question</L><In value={it.q} onChange={(v) => patch({ q: v })} /></div>
              <div><L>Answer</L><Area rows={3} value={it.a} onChange={(v) => patch({ a: v })} /></div>
            </div>
          )}
        />
      </Section>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-white border border-gray-200 rounded-sm p-4 flex items-center justify-between mt-6 shadow-md">
        <span className="text-xs text-gray-500">Changes are saved to Site Settings and immediately reflect on the public site.</span>
        <button
          type="button"
          onClick={onSave}
          disabled={loading}
          className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="cfg-home-content-save"
        >
          <Save size={14} /> {loading ? "Saving..." : "Save All Home Content"}
        </button>
      </div>
    </div>
  );
}
